import React, { useRef, useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import zhTwLocale from '@fullcalendar/core/locales/zh-tw';

import '../../../css/components/Schedule/Calendar/Calendar.css';

import { staffOrderExtendedProps, StaffOrderInfoCardList } from '../../../constants';
import { GenericModal, Message } from '../../../components';
import { TruckReturnForm } from '../../TruckLoanApplication/TruckReturnForm';
import { delay, formatTaiwanDateTime, formatTaiwanDateTimeAMPM } from '../../../utils';
import { CancelTruckLoanApplicationForm } from '../../TruckLoanApplication/CancelTruckLoanApplicationForm';
import { useUser } from '../../../hooks/useUser';

import { getVehicles } from '../../../service/api/Vehicle/apiVehicle';
import { returnDeliverOrder } from '../../../service/api/StaffOrder/apiStaffOrder';
import { getTruckLoanApplicationByDeliverOrderId, returnTruckLoanApplication, cancelTruckLoanApplication } from '../../../service/api/TruckLoanApplication/apiTruckLoanApplication';

const Calendar = ({ staffOrder, record, setEditData, loginCheck }) => {
  const now = new Date(new Date().getTime() - 3 * 24 * 60 * 60 * 1000);
  const calendarRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null); // 存放點擊事件
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [loanApplication, setLoanApplication] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const { id, name } = useUser();

  // 檢查是否已有借車申請
  const checkExistingLoanApplication = async (deliverOrderId) => {
    try {
      const response = await getTruckLoanApplicationByDeliverOrderId(deliverOrderId);
      if (response?.status === 200 && response.data?.status === 1) {
        setLoanApplication(response.data.result);
        return response.data.result;
      }
      setLoanApplication(null);
      return null;
    } catch (error) {
      setLoanApplication(null);
      return null;
    }
  };

  // 取得車輛資訊
  const fetchVehicleInfo = async (plate) => {
    try {
      const response = await getVehicles({ plate });
      if (response?.status === 200 && response.data?.result?.length > 0) {
        setVehicleInfo(response.data.result[0]);
      }
    } catch (error) {
      console.error('取得車輛資訊失敗:', error);
    }
  };

  // 當選擇事件時，取得相關資訊
  useEffect(() => {
    if (selectedEvent && selectedEvent.extendedProps?.company === '順康資產管理顧問股份有限公司') {
      checkExistingLoanApplication(selectedEvent.publicId);
      if (selectedEvent.extendedProps?.plate) {
        fetchVehicleInfo(selectedEvent.extendedProps.plate);
      }
    }
  }, [selectedEvent]);

  // 驗證借車申請狀態，回傳 loan 或 null（若有問題則顯示訊息並 return null）
  const validateLoanForAction = async (actionLabel = '進行操作') => {
    const loan = await checkExistingLoanApplication(selectedEvent.publicId);
    if (!loan) return null;
    if (loan.status === 'pending') {
      if (loan.actualCheckoutAt && loan.actualReturnAt && loan.preCheckStatus && loan.postCheckStatus)
        Message(`還車申請尚未審核通過，請等待審核後再${actionLabel}`, 'warning');
      else if (loan.isCancelRequest)
        Message('正在進行撤銷申請，無法進行操作', 'warning');
      else
        Message(`借車申請尚未審核通過，請等待審核後再${actionLabel}`, 'warning');
      return null;
    }
    // 'pending','repentant','approved','rejected','returned','voided','cancelled'
    if (loan.status !== 'approved') {
      Message(`無法進行操作`, 'warning');
      await delay(1);
      window.location.reload();
      return null;
    }
    return loan;
  };

  const handleEdit = async () => {
    const loan = await validateLoanForAction('進行操作');
    if (!loan) return;
    setEditData({ ...selectedEvent.extendedProps, id: selectedEvent.publicId, mode: selectedEvent.extendedProps.company ? 'company' : 'personal' });
    setIsModalOpen(false);
  };

  const handleCancelRequest = async () => {
    const loan = await validateLoanForAction('進行操作');
    if (!loan) return;
    setCancelModalOpen(true);
  };

  const handleReturn = async () => {
    try {
      // 如果是順康公司，需要填寫返還記錄
      if (selectedEvent.extendedProps.company === '順康資產管理顧問股份有限公司') {
        // 先檢查是否有借車申請
        const loan = await checkExistingLoanApplication(selectedEvent.publicId);
        if (!loan) {
          Message('找不到此派車單的借車申請記錄', 'warning');
          return;
        }
        if (loan.status === 'pending') {
          if (loan.actualCheckoutAt && loan.actualReturnAt && loan.preCheckStatus && loan.postCheckStatus)
            Message('還車申請尚未審核通過，請等待審核後再歸還車輛', 'warning');
          else if (loan.isCancelRequest)
            Message('正在進行撤銷申請，無法進行還車動作', 'warning');
          else
            Message('借車申請尚未審核通過，請等待審核後再歸還車輛', 'warning');
          return;
        }
        if (loan.status !== 'approved') {
          Message(`無法進行還車操作`, 'warning');
          await delay(1);
          window.location.reload();
          return;
        }
        // 顯示返還記錄表單
        setShowReturnForm(true);
        return;
      }

      // 非順康公司，直接歸還
      if (!window.confirm('歸還車輛後將無法修改派車單，確定要歸還此車輛嗎？')) {
        return;
      }
      const response = await returnDeliverOrder(selectedEvent.publicId);
      if (response?.status === 200 && response?.data?.status === 1) {
        Message(response.data.message, 'success');
        setIsModalOpen(false);
        window.location.reload();
      } else {
        Message(response?.data?.message || '歸還失敗', 'warning');
      }
    } catch (error) {
      Message('歸還過程發生錯誤: ' + error.message, 'error');
    }
  };

  const handleReturnFormSubmit = async (returnData) => {
    try {
      if (!window.confirm('確定要歸還此車輛嗎？')) {
        return;
      }

      // 1. 更新借車申請的返還記錄
      if (loanApplication) {
        const loanResponse = await returnTruckLoanApplication({
          ...returnData,
          applicationId: loanApplication.id,
          deliverId: id,
          deliverName: name,
        });

        if (loanResponse?.status !== 200 || loanResponse.data?.status !== 1) {
          Message(loanResponse?.data?.message || '更新借車申請失敗', 'warning');
          return;
        }
        Message('車輛已成功歸還', 'success');
        setShowReturnForm(false);
        setIsModalOpen(false);
        window.location.reload();
      }
    } catch (error) {
      Message('歸還過程發生錯誤: ' + error.message, 'error');
    }
  };

  const handleReturnFormCancel = () => {
    setShowReturnForm(false);
  };

  const handleCancelApplication = async (data) => {
    try {
      const response = await cancelTruckLoanApplication({
        applicationId: loanApplication.id,
        cancelReason: data.cancelReason,
        userId: id,
        userName: name,
      });
      if (response?.status === 200 && response.data.status === 1) {
        Message('已提交取消申請', 'success');
        setCancelModalOpen(false);
        setIsModalOpen(false);
        window.location.reload();
      } else {
        Message(response?.data?.message || '取消申請失敗', 'warning');
      }
    } catch (error) {
      Message('取消申請錯誤: ' + error.message, 'error');
    }
  };

  const calendarEvents = staffOrder.map(data => {
    const extendedProps = {};
    staffOrderExtendedProps.forEach(field => {
      extendedProps[field] = data[field];
    });
    const usedAtDate = new Date(formatTaiwanDateTime(data.usedAt));
    const isAfter = usedAtDate < now;

    return {
      id: data.id,
      title: `${data.name} - ${data.plate}`,
      // usedAt: data.usedAt.slice(0, 10),
      start: formatTaiwanDateTime(data.usedAt),
      ...extendedProps,
      isAfter,
      // classNames: isAfter ? ['after-event'] : data.status === 1 ? data.isReturned === "未歸還" ? [] : ['after-event'] : [],
      color: data.status === 1 ? data.isReturned === "未歸還" ? "#ebb521" : "#00a738" : data.status === 2 ? "#5e5e5e" : "#ff0800"
    }
  });

  return (
    <>
      <div className="ms-2 me-2">
        {/* 顏色提示說明 */}
        <div className="calendar-legend mb-3 ms-4 me-4">
          <div>
            <div className="calendar-legend-color" style={{ backgroundColor: '#ebb521' }}></div>
            <span>未歸還（已完成）</span>
          </div>
          <div>
            <div className="calendar-legend-color" style={{ backgroundColor: '#00a738' }}></div>
            <span>已歸還（已完成）</span>
          </div>
          <div>
            <div className="calendar-legend-color" style={{ backgroundColor: '#5e5e5e' }}></div>
            <span>待審核</span>
          </div>
          <div>
            <div className="calendar-legend-color" style={{ backgroundColor: '#ff0800' }}></div>
            <span>未歸還（未完成）</span>
          </div>
        </div>
        <div className="CalendarLayout">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale={zhTwLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth'
            }}
            height="auto"
            dayMaxEvents={5}
            events={calendarEvents}
            eventClick={info => {
              // if (info.event.extendedProps.isAfter) {
              //   return;
              // }
              console.log({ ...info.event._def, ...info.event.extendedProps })
              setSelectedEvent({ ...info.event._def, ...info.event.extendedProps });
              setIsModalOpen(true);
            }}
          />
        </div>
      </div>
      {
        selectedEvent && (
          <GenericModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            children={
              <div className="card order-info-card">
                {
                  StaffOrderInfoCardList.map(item => {
                    if (item.field === 'usedAt' || item.field === 'returnAt' || item.field === 'finishAt') {

                      return <div className="mb-2" key={item.field}>
                        <strong>{item.name}</strong>
                        <span >
                          {formatTaiwanDateTimeAMPM(selectedEvent[item.field])}
                        </span>
                      </div>
                    } else if (item.field === 'status') {
                      return <div className="mb-2" key={item.field}>
                        <strong>{item.name}</strong>
                        <span className={`status ${selectedEvent[item.field] ? selectedEvent[item.field] === 1 ? 'approved' : 'unReviewed' : 'rejected'}`}>
                          {selectedEvent[item.field] === 1 ? "已完成" : selectedEvent[item.field] === 1 ? "已完成" : "未完成"}
                        </span>
                      </div>
                    } else if (item.field === 'title') {
                      return <h5 key={item.field}>{selectedEvent[item.field]}</h5>
                    } else {
                      return <div key={item.field} className="mb-2"><strong>{item.name}</strong> <span>{selectedEvent[item.field] ? selectedEvent[item.field] : "-"}</span></div>
                    }
                  })
                }
                <div className="mb-2 d-flex justify-content-center gap-2">
                  {loginCheck?.status === 1 ? (
                    selectedEvent.extendedProps.name === loginCheck.name ? (
                      selectedEvent.extendedProps.status !== 1 ? (
                        // 未完成：顯示編輯、申請撤銷
                        <>
                          <button className="driver-btn" onClick={handleEdit}>編輯</button>
                          <button className="driver-btn btn-warning" onClick={handleCancelRequest}>申請撤銷</button>
                        </>
                      ) : (
                        // 已完成：若未歸還且為順康，顯示歸還車輛
                        selectedEvent.extendedProps.isReturned === "未歸還" &&
                        selectedEvent.extendedProps.company === '順康資產管理顧問股份有限公司' && (
                          <button className="driver-btn btn-return" onClick={handleReturn}>歸還車輛</button>
                        )
                      )
                    ) : null // 不是自己的訂單，不顯示任何按鈕
                  ) : '請先登入後才可修改'}
                </div>
                {loginCheck?.status === 1 &&
                  selectedEvent.extendedProps.name === loginCheck.name &&
                  selectedEvent.extendedProps.status !== 1 &&
                  selectedEvent.extendedProps.isReturned !== "已歸還" &&
                  selectedEvent.extendedProps.company === '順康資產管理顧問股份有限公司' && (
                    <button className="driver-btn btn-return" onClick={handleReturn}>完成並歸還車輛</button>
                  )
                }
              </div >
            }
          />

        )
      }

      {/* 申請取消 Modal */}
      <GenericModal isOpen={cancelModalOpen}>
        <div className="truckLoanApplicationForm custom-modal-backdrop">
          <div className="custom-modal" style={{ width: '1200px', maxHeight: '90vh', overflow: 'auto' }}>
            <CancelTruckLoanApplicationForm
              deliverOrder={selectedEvent?.extendedProps}
              loanApplication={loanApplication}
              onSubmit={handleCancelApplication}
              onCancel={() => setCancelModalOpen(false)}
            />
          </div>
        </div>
      </GenericModal>

      {/* 車輛返還記錄表單 Modal */}
      {showReturnForm && selectedEvent && (
        <GenericModal
          isOpen={showReturnForm}
          onClose={handleReturnFormCancel}
          children={
            <div className="truckLoanApplicationForm custom-modal-backdrop">
              <div className="custom-modal" style={{ width: '1200px', maxHeight: '90vh', overflow: 'auto' }}>
                <TruckReturnForm
                  deliverOrder={{
                    id: selectedEvent.publicId,
                    plate: selectedEvent.extendedProps?.plate,
                    name: selectedEvent.extendedProps?.name,
                    usedAt: selectedEvent.extendedProps?.usedAt,
                    finishAt: selectedEvent.extendedProps?.finishAt,
                  }}
                  vehicleInfo={vehicleInfo}
                  loanApplication={loanApplication}
                  userInfo={{
                    id: loginCheck?.id,
                    name: loginCheck?.name,
                    phone: loginCheck?.phone,
                  }}
                  onSubmit={handleReturnFormSubmit}
                  onCancel={handleReturnFormCancel}
                />
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

export { Calendar };
